import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import {
  CourseContent,
  CourseFormat,
  CourseFormState,
  CourseStatus,
  MaterialType,
} from '@/client/courses';
import { Tag } from '@/client/tags';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { ContentForm } from '@/components/courses/forms/ContentForm';
import { useSaveCourse, useUpdateCourse } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { FlexContainer } from '@/ui/styled-ui';
import {
  generateCourseTranslationsInitialValues,
  getContentItemId,
  parseModifiedCourseFiles,
  parseModifiedCourseTranslations,
  queryStateConverter,
} from '@/utils/helpers';
import { AxiosError } from 'axios';
import { FormikValues } from 'formik';
import { MenuItem } from 'primereact/menuitem';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const getBreadcrumbs = (
  account: Account,
  t: TranslationFunctionType,
): MenuItem[] => [
  {
    label: account?.name,
    url: !account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
      : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('content'),
    url: RedirectPaths[RedirectPathsEnum.COURSES](),
    template: AppBreadCrumbTemplate,
  },
  {
    label: t('content.new'),
    url: RedirectPaths[RedirectPathsEnum.COURSES_CREATE](),
    template: AppBreadCrumbTemplate,
  },
];

export const CreateCoursePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const account = useAppSelector(selectCurrentAccount);
  const { id: courseId } = useParams();

  const [formInitialValues] = useState<CourseFormState>({
    courseTranslations: generateCourseTranslationsInitialValues(),
    courseLanguages: [],
    courseThumbnail: '',
    courseFiles: [
      {
        translations: {},
      },
    ],
    courseContents: [],
    format: CourseFormat.CONTENTS,
    courseCreatedAt: undefined,
    courseLastUpdatedAt: undefined,
    type: MaterialType.COURSE,
    isStandardCatalogue: false,
    releaseDate: null,
    customContent: false,
    customAccount: null,
    selfHosted: true,
    hubspotProperty: undefined,
    hubspotDateProperty: undefined,
    priority: null,
    label: undefined,
    isPreviewable: false,
    exemptFromMinimumRequirementScore: false,
    isCloneable: false,
    isAiCustomizable: false,
    plannerScheduleDate: null,
    tags: [],
    contentSeries: undefined,
  });

  const createCourse = useSaveCourse();
  const updateCourse = useUpdateCourse();
  const submitFormRef = useRef<(() => Promise<void>) | null>(null);

  const handleSubmit = async (data: FormikValues) => {
    try {
      if (!account) return;

      const courseData = {
        courseTranslations: parseModifiedCourseTranslations(
          data.courseTranslations,
        ),
        courseLanguages: data.courseLanguages,
        courseThumbnail: data.courseThumbnail ? data.courseThumbnail : null,
        courseFiles: parseModifiedCourseFiles(data.courseFiles, {
          selfHosted: data.selfHosted,
          courseLanguages: data.courseLanguages,
        }),
        courseContents: data?.courseContents?.map(
          (item: CourseContent, index: number) => ({
            ...item,
            id: item.id?.includes('temp') ? undefined : item.id,
            position: index,
            quiz: getContentItemId(item.quiz),
            resource: getContentItemId(item.resource),
            presentation: getContentItemId(item.presentation),
          }),
        ),
        format: CourseFormat.CONTENTS,
        courseCreatedAt: data.courseCreatedAt,
        courseLastUpdatedAt: data.courseLastUpdatedAt,
        type: data.type,
        isStandardCatalogue: data.isStandardCatalogue,
        releaseDate: data.releaseDate,
        account: account.isSystem
          ? data.customContent && !!data.customAccount
            ? data.customAccount
            : null
          : account.id,
        hubspotProperty: data.hubspotProperty,
        hubspotDateProperty: data.hubspotDateProperty,
        priority: data?.priority,
        label: data?.label ? data.label : null,
        isPreviewable: !!data?.isPreviewable,
        exemptFromMinimumRequirementScore:
          data.exemptFromMinimumRequirementScore,
        isCloneable: data.isCloneable,
        isAiCustomizable: data.isAiCustomizable,
        status: CourseStatus.SAVED,
        plannerScheduleDate: data.plannerScheduleDate,
        tags: data.tags?.map((tag: Tag) => tag.id),
        contentSeries: data.contentSeries,
      };

      const response = courseId
        ? await updateCourse.update({ courseId, updates: courseData })
        : await createCourse.create(courseData);

      toast?.success(t('toast.success'), t('content.created'));

      navigate(RedirectPaths[RedirectPathsEnum.COURSES_CONTENT](response.id));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  return (
    <>
      <AppBreadCrumb model={getBreadcrumbs(account as Account, t)} />
      <FlexContainer justify="flex-start">
        <ContentForm
          initialValues={formInitialValues}
          onSubmit={handleSubmit}
          state={queryStateConverter(createCourse)}
          isCreate
          selfHosted
          submitFormRef={submitFormRef}
        />
      </FlexContainer>
    </>
  );
};
